import type { SignInPath } from "./fleetAuth";

/**
 * Which door an email address belongs to, decided before leaving the console.
 *
 * The two populations have strictly separate ways in (see SignInPath), and the
 * split is by domain: staff are on the corporate domain and federate through
 * Google Workspace; everyone else has a native email + password account. The
 * server already enforces this -- platform_auth's federation trigger accepts a
 * Google sign-in only from `allowed_domains` -- so deciding it here changes no
 * outcome, only where a wrong pick is caught: on this page, with a Back link,
 * instead of on Cognito's password card, which has no way back (its only link
 * is "Forgot your password?", and managed login cannot carry custom text).
 *
 * Mirror of `allowed_domains` in platform_auth/config/<env>.yaml. It is a
 * literal rather than a build variable because the domain is the company's,
 * not the environment's, and a second source of truth that could drift from
 * the trigger is what this list must not become: keep it to the one entry.
 */
export const EMPLOYEE_EMAIL_DOMAINS: readonly string[] = ["aerovect.com"];

export interface EmailDoor {
  path: SignInPath;
  /** The address as typed, minus surrounding whitespace. */
  email: string;
}

// One @, something either side, and a dot in the domain. Deliberately loose:
// the identity provider validates the address for real; this only has to
// refuse what could not possibly be one, so a slip is caught before the
// redirect rather than as a rejection on the other side of it.
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The door for `raw`, or null when it is not an email address at all. */
export function doorForEmail(raw: string): EmailDoor | null {
  const email = raw.trim();
  if (!LOOKS_LIKE_EMAIL.test(email)) return null;
  const domain = email.slice(email.lastIndexOf("@") + 1).toLowerCase();
  return {
    path: EMPLOYEE_EMAIL_DOMAINS.includes(domain) ? "employee" : "client",
    email,
  };
}
